/**
 * Time formatting utilities for display.
 * Accepts time strings in HH:MM or HH:MM:SS format.
 */

/** Formats time as HH:MM, stripping seconds if present (e.g. 16:00:00 → 16:00) */
export function formatTimeHHMM(time: string): string {
  if (!time) return '';
  const [h, m] = time.split(':');
  return `${h}:${(m ?? '00').padStart(2, '0')}`;
}

/**
 * Normalizes to strict HH:MM for lesson API and CSV import/export
 * (same pattern as lesson create validation: five characters, e.g. 09:05).
 * Strips seconds/milliseconds and zero-pads hours and minutes.
 */
export function toApiLessonTime(time: string): string {
  if (!time || !time.trim()) return '';
  const parts = time.trim().split(':');
  if (parts.length < 2) return time.trim();
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return time.trim();
  const hh = h.toString().padStart(2, '0');
  const mm = m.toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

/** Formats time in 12-hour format (e.g. "4:00 PM") for display and tooltips */
export function formatTime(time: string): string {
  if (!time) return '';
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  return `${displayHours}:${(minutes ?? 0).toString().padStart(2, '0')} ${period}`;
}

/** Alias for formatTime - use when the intent is tooltip display */
export const formatTimeForTooltip = formatTime;

/** Converts time string (HH:MM or HH:MM:SS) to minutes since midnight */
export function timeToMinutes(time: string): number {
  if (!time) return 0;
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + (minutes || 0);
}

/** Converts minutes since midnight to time string (HH:MM) */
export function minutesToTime(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}
